import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/base/TopicCard";
import { categories } from "@/mocks/site";
import { topics } from "@/mocks/topics";
import type { TopicStatus } from "@/types/news";
import { statusMeta } from "@/utils/news";

type SortKey = "heat" | "updated";

const statusFilters: { key: TopicStatus | "all"; label: string }[] = [
  { key: "all", label: "全部状态" },
  { key: "tracking", label: "追踪中" },
  { key: "verifying", label: "核查中" },
  { key: "settled", label: "已核实" },
];

export default function CategoryPage() {
  const { slug = "reyi" } = useParams();
  const category = categories.find((item) => item.slug === slug) ?? categories[0];

  const [sort, setSort] = useState<SortKey>("updated");
  const [status, setStatus] = useState<TopicStatus | "all">("all");

  const list = useMemo(() => {
    const filtered = topics.filter(
      (topic) =>
        topic.category === category.slug && (status === "all" || topic.status === status),
    );
    return filtered.sort((a, b) =>
      sort === "heat" ? b.heat - a.heat : b.updatedAt.localeCompare(a.updatedAt),
    );
  }, [category.slug, sort, status]);

  const allInCategory = topics.filter((topic) => topic.category === category.slug);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name} - 蝌蚪爆料`,
      description: category.desc,
      url: `https://dfjor.cn/category/${category.slug}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://dfjor.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: category.name,
          item: `https://dfjor.cn/category/${category.slug}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${category.name} - 蝌蚪爆料 | 热点事件的${category.short}与梳理`}
        description={`${category.desc}蝌蚪爆料${category.name}栏目持续更新，梳理事件进展、争议焦点与当事方回应，帮你快速看清重点。`}
        keywords={`蝌蚪爆料,${category.name},热议事件追踪,${category.short}`}
        path={`/category/${category.slug}`}
        jsonLd={structuredData}
      />

      <section className="relative overflow-hidden bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src={category.cover}
            alt={`${category.name}栏目封面`}
            title={`${category.name} - 蝌蚪爆料`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/78 to-foreground-950/45"></div>
        </div>

        <div className="relative px-4 py-10 md:px-6 md:py-14 lg:px-8">
          <div className="text-background-300">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: category.name }]}
            />
          </div>
          <div className="mt-5 flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`} aria-hidden="true"></i>
            </span>
            <div>
              <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
                {category.desc}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  item.slug === category.slug
                    ? "border-primary-400 bg-primary-500 text-background-50"
                    : "border-background-500/50 text-background-200 hover:border-accent-400 hover:text-accent-300"
                }`}
              >
                <i className={`${item.icon} text-sm`} aria-hidden="true"></i>
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-10 md:px-6 md:py-12 lg:px-8">
        <div className="flex flex-col gap-4 border-b border-background-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm text-foreground-600">
            本栏目共 <span className="font-semibold text-foreground-950">{list.length}</span> 个事件
            {status !== "all" && `（筛选后）`}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex flex-wrap items-center gap-2">
              {statusFilters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setStatus(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    status === item.key
                      ? "border-primary-300 bg-primary-100 text-primary-800"
                      : "border-background-300 text-foreground-600 hover:border-primary-200"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center rounded-full bg-background-200 px-1 py-1">
              {(["updated", "heat"] as SortKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSort(key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    sort === key
                      ? "bg-background-50 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {key === "updated" ? "按更新时间" : "按热度"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {list.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              这个状态下暂时没有事件
            </p>
            <p className="mt-2 text-sm text-foreground-600">换个状态或切换排序再看看。</p>
            <button
              type="button"
              onClick={() => setStatus("all")}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部状态
            </button>
          </div>
        )}
      </section>

      <section className="px-4 pb-12 md:px-6 lg:px-8">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">栏目概览</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {statusFilters
              .filter((item) => item.key !== "all")
              .map((item) => {
                const meta = statusMeta(item.key as TopicStatus);
                const count = allInCategory.filter((topic) => topic.status === item.key).length;
                return (
                  <span
                    key={item.key}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${meta.className}`}
                  >
                    <i className={`${meta.icon} text-sm`} aria-hidden="true"></i>
                    {meta.label} · {count}
                  </span>
                );
              })}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            处于「追踪中」的事件会优先更新；「核查中」表示材料仍在比对，结论可能变化；
            「已核实」表示已有明确公开信息支撑，只在出现重大变化时更新。
          </p>
        </div>
      </section>
    </>
  );
}